import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { Ban, Download, ExternalLink, FileText, Pencil } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Pill } from "components/ui/Pill";
import { Select } from "components/ui/Select/Select";
import { usePatientData } from "features/patients/hooks/usePatientData";
import {
  formatCdmStatus,
  getCdmStatusColors,
  resolveCdmReviewStatus,
} from "utils/cdmStatus";

import { useStaffInfo } from "../../../practice/hooks/useStaffInfo";
import { diagnosisItems as cdmDiagnosisItems } from "../../cdm/constants/diagnosisItems";
import { CancelReviewModal } from "../../components/CancelReviewModal/CancelReviewModal";
import { PCRS_CONNECTION_REQUIRED_MESSAGE } from "../../constants/pcrsSchemeCheck";
import {
  getReviewTypeTitle,
  isCdm,
  isDeregistration,
  isOcf,
  isPp,
  isRegistration,
} from "../../constants/reviewTypes";
import { useCancelReview } from "../../hooks/useCancelReview";
import { useCdmReviewDiseaseIds } from "../../hooks/useCdmReviewDiseaseIds";
import { useDownloadCdmClinicalExportPdf } from "../../hooks/useDownloadCdmClinicalExportPdf";
import { diagnosisItems as ppDiagnosisItems } from "../../pp/constants/diagnosisItems";
import { isCdmReviewEligible } from "../../utils/cdmEligibility";
import { formatCorrectedDate } from "../../utils/reviewDate";
import { isOwnReview, NOT_OWNER_MESSAGE } from "../../utils/reviewOwnership";
import styles from "./ReviewsHistoryTable.module.css";

const getProgramme = (reviewType) => {
  if (isCdm(reviewType)) return "CDM";
  if (isOcf(reviewType)) return "OCF";
  if (isPp(reviewType)) return "PP";

  return null;
};

const ACKNOWLEDGED = "ACKNOWLEDGED";

/**
 * Clinical-data PDF export count, latest-first. Patient-level choice (the export
 * endpoint is patient-scoped, not per-review). BE clamps server-side to [1,3].
 */
const EXPORT_COUNT_OPTIONS = [
  { label: "Latest review", value: "1" },
  { label: "Last 2 reviews", value: "2" },
  { label: "Last 3 reviews", value: "3" },
];
const DEFAULT_EXPORT_COUNT = "1";

/**
 * Correct (Edit) / Cancel are allowed only when BOTH messages are acknowledged
 * (BE contract — see .claude/cdm-context/13-backend-api-spec.md) and only on the
 * most-recent review of its programme. BE also enforces most-recent + own; we
 * mirror most-recent here from the (descending) list ordering so blocked actions
 * are hidden up front.
 */
const canActionReview = (item) =>
  item.clinicalMessageStatus === ACKNOWLEDGED &&
  item.reimbursementMessageStatus === ACKNOWLEDGED &&
  !!item.isMostRecentForProgramme;

/**
 * Reason a review cannot be cancelled, or null when it can. Cancel is always
 * shown (where Delete used to be) but disabled with this message as a tooltip.
 *
 * Cancel is limited to a programme's REGISTRATION review that has NO subsequent
 * reviews: an interim/annual/subsequent review can never be cancelled, and a
 * registration that already has later reviews (so it is not the most-recent for
 * its programme) is also blocked.
 */
const getCancelDisabledReason = (item) => {
  if (!getProgramme(item.reviewType)) {
    return "This review type cannot be cancelled.";
  }

  if (!isRegistration(item.reviewType)) {
    return "Only a programme's registration review can be cancelled.";
  }

  if (item.isOwnReview === false) {
    return NOT_OWNER_MESSAGE;
  }

  if (!item.isMostRecentForProgramme) {
    return "This registration cannot be cancelled — the programme has subsequent reviews.";
  }

  if (
    item.clinicalMessageStatus !== ACKNOWLEDGED ||
    item.reimbursementMessageStatus !== ACKNOWLEDGED
  ) {
    return "You can cancel only once both messages are acknowledged.";
  }

  if (!item.reimbursementMessageControlId) {
    return "This review cannot be cancelled — no reimbursement message.";
  }

  return null;
};

const OCF_INDICATOR_LABELS = {
  currentSmokingStatus: "Current Smoking Status",
  bmiGreaterThan30: "BMI >=30kg/m2",
  previousHighBnp: "Previous BNP >=34pg/ml or NTproBNP >=125pg/ml",
  highRiskEthnicity: "Ethnicity",
  historyOfGestationalDiabetes: "History of Gestational Diabetes",
  dyslipidaemia: "Dyslipidaemia (previously recorded)",
  chronicKidneyDisease: "Chronic kidney disease",
  historyOfSevereMentalIllness: "History of severe Mental Illness",
};

const getIndicatorLabels = (data, reviewType) => {
  if (!data?.clinicalData) return [];

  if (isDeregistration(reviewType)) {
    return data.clinicalData?.reasonDescription
      ? [data.clinicalData.reasonDescription]
      : [];
  }

  if (isCdm(reviewType)) {
    const diagnoses = data.clinicalData?.diagnosis?.diagnoses ?? [];
    return diagnoses
      .map((d) => cdmDiagnosisItems.find((item) => item.id === d.disease)?.name)
      .filter(Boolean);
  }

  if (isOcf(reviewType)) {
    const indications = data.clinicalData?.indications ?? {};
    const labels = Object.entries(OCF_INDICATOR_LABELS)
      .filter(([field]) => indications[field])
      .map(([, label]) => label);

    if (indications.otherText) labels.push(`Other: ${indications.otherText}`);

    return labels;
  }

  if (isPp(reviewType)) {
    const reasons =
      data.clinicalData?.clinicalDetails?.registrationReasons ?? [];
    return reasons
      .map((id) => ppDiagnosisItems.find((item) => item.id === id)?.name)
      .filter(Boolean);
  }

  return [];
};

const formatDate = (date) => {
  if (!date) return "-";
  try {
    return format(new Date(date), "dd-MM-yyyy");
  } catch {
    return "-";
  }
};

const useReviewDetails = (reviews) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const queries = useQueries({
    queries: (reviews ?? []).map((review) => ({
      queryKey: ["reviewDetails", review.id, tenantVal],
      queryFn: async () => {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/cdm/reviews/${review.id}`
        );
        const data = response.data;
        return {
          status: resolveCdmReviewStatus(data),
          clinicalMessageStatus: data.clinicalMessageStatus ?? null,
          reimbursementMessageStatus: data.reimbursementMessageStatus ?? null,
          indicators: getIndicatorLabels(data, review.reviewType),
          reimbursementMessageControlId:
            data.reimbursementMessageControlId ?? null,
          correctedDate: data.correctedDate ?? null,
          doctorId: data.doctorId ?? null,
          doctorName: data.doctorName ?? null,
        };
      },
      enabled: !!review.id && !!tenantVal,
      staleTime: 5 * 60 * 1000,
    })),
  });

  return useMemo(() => {
    const detailsById = {};
    (reviews ?? []).forEach((review, index) => {
      const result = queries[index];
      if (result?.data != null) {
        detailsById[review.id] = result.data;
      }
    });
    return detailsById;
  }, [reviews, queries]);
};

export const ReviewsHistoryTable = ({
  reviews,
  patientId,
  onOpenPreview,
  onOpenEdit,
  onOpenCarePlan,
  actionsDisabled = false,
}) => {
  const [cancelConfirmItem, setCancelConfirmItem] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportCount, setExportCount] = useState(DEFAULT_EXPORT_COUNT);

  const { data: currentStaff } = useStaffInfo();
  const { data: patient } = usePatientData(patientId);

  // Private patients may only be on the Diabetes CDM programme — drop any
  // non-Diabetes CDM reviews from the history. OCF/PP/deregistration untouched.
  const cdmDiseaseIdsByReview = useCdmReviewDiseaseIds(reviews);

  const visibleReviews = useMemo(
    () =>
      (reviews ?? []).filter((review) => {
        if (!isCdm(review.reviewType)) return true;

        return isCdmReviewEligible(
          cdmDiseaseIdsByReview[review.id],
          patient?.financialClass
        );
      }),
    [reviews, cdmDiseaseIdsByReview, patient?.financialClass]
  );
  const currentStaffId = currentStaff?.staffMember?.id ?? null;
  const currentStaffName = currentStaff?.staffMember
    ? `${currentStaff.staffMember.firstName} ${currentStaff.staffMember.lastName}`
    : null;

  const cancelCdm = useCancelReview({ patientId, programme: "CDM" });
  const cancelOcf = useCancelReview({ patientId, programme: "OCF" });
  const cancelPp = useCancelReview({ patientId, programme: "PP" });

  const { mutate: downloadClinicalExport, isPending: isExporting } =
    useDownloadCdmClinicalExportPdf();

  const cancelMutationByProgramme = {
    CDM: cancelCdm,
    OCF: cancelOcf,
    PP: cancelPp,
  };

  const isCancelling =
    cancelCdm.isPending || cancelOcf.isPending || cancelPp.isPending;

  const handleConfirmCancel = () => {
    if (!cancelConfirmItem) return;

    const programme = getProgramme(cancelConfirmItem.reviewType);
    const mutation = cancelMutationByProgramme[programme];

    if (!mutation || !cancelConfirmItem.reimbursementMessageControlId) {
      setCancelConfirmItem(null);

      return;
    }

    mutation.mutate(
      {
        originalReimbursementMessageControlId:
          cancelConfirmItem.reimbursementMessageControlId,
      },
      { onSettled: () => setCancelConfirmItem(null) }
    );
  };

  const handleDownloadExport = () => {
    downloadClinicalExport(
      {
        patientId,
        tenantVal: localStorage.getItem("kinesin-tenant"),
        count: Number(exportCount),
      },
      { onSuccess: () => setIsExportModalOpen(false) }
    );
  };

  const sorted = useMemo(
    () =>
      [...visibleReviews].sort(
        (a, b) =>
          new Date(b.reviewDate ?? b.date) - new Date(a.reviewDate ?? a.date)
      ),
    [visibleReviews]
  );

  const detailsById = useReviewDetails(visibleReviews);

  const reviewsWithDetails = useMemo(() => {
    const seenProgrammes = new Set();

    return sorted.map((review) => {
      const programme = getProgramme(review.reviewType);
      const isMostRecentForProgramme =
        !!programme && !seenProgrammes.has(programme);

      if (programme) seenProgrammes.add(programme);

      return {
        ...review,
        status:
          detailsById[review.id]?.status ??
          resolveCdmReviewStatus(review),
        clinicalMessageStatus:
          detailsById[review.id]?.clinicalMessageStatus ??
          review.clinicalMessageStatus ??
          null,
        reimbursementMessageStatus:
          detailsById[review.id]?.reimbursementMessageStatus ??
          review.reimbursementMessageStatus ??
          null,
        indicators: detailsById[review.id]?.indicators ?? [],
        reimbursementMessageControlId:
          detailsById[review.id]?.reimbursementMessageControlId ??
          review.reimbursementMessageControlId ??
          null,
        correctedDate:
          detailsById[review.id]?.correctedDate ?? review.correctedDate ?? null,
        isMostRecentForProgramme,
        isOwnReview: isOwnReview({
          doctorId: detailsById[review.id]?.doctorId,
          doctorName: detailsById[review.id]?.doctorName ?? review.doctorName,
          staffId: currentStaffId,
          staffName: currentStaffName,
        }),
      };
    });
  }, [sorted, detailsById, currentStaffId, currentStaffName]);

  const columns = useMemo(
    () => [
      {
        id: "reviewType",
        header: "Type",
        width: "230px",
        render: (item) => getReviewTypeTitle(item.reviewType) || "-",
      },
      {
        id: "indicators",
        header: "Indications for Review / Diagnosis / Reason",
        width: "minmax(0, 400px)",
        render: (item) => {
          const labels = item.indicators ?? [];
          if (labels.length === 0) return "-";
          const text = labels.join(", ");
          return (
            <div
              title={labels.join("\n")}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "500px",
              }}
            >
              {text}
            </div>
          );
        },
      },
      {
        id: "reviewDate",
        header: "Date",
        width: "200px",
        render: (item) => {
          const correctedOn = formatCorrectedDate(item.correctedDate);

          return (
            <div className={styles.dateCell}>
              <span>{formatDate(item.reviewDate ?? item.date)}</span>
              {!!correctedOn && (
                <span className={styles.correctedOn}>
                  Corrected on {correctedOn}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        width: "minmax(200px, 1fr)",
        render: (item) => {
          const colors = getCdmStatusColors(item.status);
          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {formatCdmStatus(item.status)}
            </Pill>
          );
        },
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Open preview",
        icon: ExternalLink,
        primary: true,
        onClick: (item) => onOpenPreview(item.id),
      },
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        visibleWhen: (item) => canActionReview(item),
        disabled: (item) => item.isOwnReview === false || actionsDisabled,
        tooltip: (item) =>
          actionsDisabled
            ? PCRS_CONNECTION_REQUIRED_MESSAGE
            : item.isOwnReview === false
            ? NOT_OWNER_MESSAGE
            : "Edit",
        onClick: (item) => onOpenEdit(item.id),
      },
      {
        label: "Care plan",
        icon: FileText,
        visibleWhen: (item) => isCdm(item.reviewType) || isPp(item.reviewType),
        onClick: (item) => onOpenCarePlan?.(item.id),
      },
      {
        label: "Download clinical data (PDF)",
        icon: Download,
        visibleWhen: (item) => isCdm(item.reviewType) || isPp(item.reviewType),
        onClick: () => {
          setExportCount(DEFAULT_EXPORT_COUNT);
          setIsExportModalOpen(true);
        },
      },
      {
        label: "Cancel review",
        icon: Ban,
        primary: true,
        disabled: (item) => !!getCancelDisabledReason(item) || actionsDisabled,
        tooltip: (item) =>
          actionsDisabled
            ? PCRS_CONNECTION_REQUIRED_MESSAGE
            : getCancelDisabledReason(item) || "Cancel review",
        onClick: (item) => setCancelConfirmItem(item),
      },
    ],
    [onOpenPreview, onOpenEdit, onOpenCarePlan, actionsDisabled]
  );

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>Review History</h3>
      <DataTable
        columns={columns}
        data={reviewsWithDetails}
        rowActions={rowActions}
        emptyTitle="No reviews yet"
        emptyText="Submitted reviews will appear here."
        rowsPerPage={20}
        rowsOptions={[20, 50, "All"]}
      />
      {isExportModalOpen && (
        <Dialog
          open
          title="Download clinical data"
          onClose={() => setIsExportModalOpen(false)}
          width={440}
        >
          <div className={styles.exportModalBody}>
            <p className={styles.exportModalText}>
              Select how many of the most recent reviews to include in the PDF
              (one A4 page per review).
            </p>
            <div>
              <span className={styles.exportLabel}>Reviews to include</span>
              <Select
                options={EXPORT_COUNT_OPTIONS}
                value={exportCount}
                onChange={setExportCount}
                disabled={isExporting}
                fullWidth
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button onClick={handleDownloadExport} disabled={isExporting}>
              <Download size={16} />
              {isExporting ? "Downloading…" : "Download"}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
      <CancelReviewModal
        isOpen={!!cancelConfirmItem}
        isCancelling={isCancelling}
        onClose={() => setCancelConfirmItem(null)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};
